// src/components/site-editor/panels/FAQsPanel.js
import React, { useState } from 'react';
import { Plus, Edit2, Trash2, ChevronUp, ChevronDown, HelpCircle } from 'lucide-react';
import { useWebsiteBuilder } from '../../../contexts/WebsiteBuilderContext';
import InlineCallout from '../InlineCallout';
import Toggle from '../../Toggle';

// Default FAQ suggestions with pre-filled answers
const DEFAULT_FAQ_SUGGESTIONS = [
  {
    question: "What should I expect during a session?",
    answer: "During our session, you can expect a warm, judgment-free space. I'll guide you through the process and explain everything as we go. Each session is unique to your needs and intentions."
  },
  {
    question: "How do I prepare for my appointment?",
    answer: "Come with an open mind and any specific questions or intentions you'd like to explore. Wear comfortable clothing and find a quiet space if we're meeting virtually."
  },
  {
    question: "Do you offer virtual sessions?",
    answer: "Yes! I offer both virtual and in-person sessions. Virtual sessions are conducted via Zoom and are just as effective as meeting in person."
  },
  {
    question: "What is your cancellation policy?",
    answer: "Please provide at least 24 hours notice if you need to cancel or reschedule. Late cancellations may be subject to a fee."
  },
  {
    question: "How long are sessions and what do they cost?",
    answer: "Session lengths and pricing vary depending on the type of service. Please visit my Services section above for current offerings and rates."
  },
  {
    question: "Is everything we discuss confidential?",
    answer: "Absolutely. Everything shared during our sessions is held in strict confidence. Your privacy and trust are my top priorities."
  },
  {
    question: "Do I need any prior experience?",
    answer: "No prior experience is needed! I welcome both newcomers and experienced seekers. I'll meet you wherever you are on your journey."
  },
  {
    question: "How do I book a follow-up session?",
    answer: "You can book your next session directly through my website using the booking button. I recommend scheduling your follow-up before leaving our current session so we can maintain momentum."
  }
];

const FAQsPanel = () => {
  const { state, actions } = useWebsiteBuilder();
  const faqs = state.draft.content.faqs || [];

  const [editingFaq, setEditingFaq] = useState(null); // null | 'new' | faqId
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleAddFaq = () => {
    setEditingFaq('new');
  };

  const handleEditFaq = (faqId) => {
    setEditingFaq(faqId);
  };

  const handleDeleteFaq = (faqId) => {
    const updatedFaqs = faqs.filter(f => f.id !== faqId);
    actions.updateContent('faqs', updatedFaqs);
    setDeleteConfirm(null);
  };

  const handleToggleActive = (faqId) => {
    const updatedFaqs = faqs.map(f =>
      f.id === faqId ? { ...f, active: !f.active } : f
    );
    actions.updateContent('faqs', updatedFaqs);
  };

  const handleReorder = (faqId, direction) => {
    const currentIndex = faqs.findIndex(f => f.id === faqId);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === faqs.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const updatedFaqs = [...faqs];
    const [movedFaq] = updatedFaqs.splice(currentIndex, 1);
    updatedFaqs.splice(newIndex, 0, movedFaq);

    // Update order property
    const reorderedFaqs = updatedFaqs.map((faq, index) => ({
      ...faq,
      order: index
    }));

    actions.updateContent('faqs', reorderedFaqs);
  };

  const handleAddSuggestedFaq = (suggestion) => {
    const newFaq = {
      id: `faq_${Date.now()}`,
      question: suggestion.question,
      answer: suggestion.answer,
      order: faqs.length,
      active: true,
      createdAt: new Date().toISOString()
    };
    actions.updateContent('faqs', [...faqs, newFaq]);
    // Open the editor for the newly added FAQ so they can customize it
    setEditingFaq(newFaq.id);
  };

  // Get available suggestions (filter out questions that already exist)
  const availableSuggestions = DEFAULT_FAQ_SUGGESTIONS.filter(
    suggestion => !faqs.some(faq => faq.question.toLowerCase() === suggestion.question.toLowerCase())
  );

  // Show suggestions while any remain available
  const showSuggestions = availableSuggestions.length > 0 && editingFaq === null;

  return (
    <div className="p-6 space-y-6">
      {/* Section Visibility */}
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <div>
          <p className="text-sm font-medium text-gray-900">Show section on website</p>
          <p className="text-xs text-gray-500 mt-0.5">Toggle off to hide without deleting</p>
        </div>
        <Toggle
          enabled={state.draft.sectionVisibility?.faqs ?? true}
          onChange={() => actions.toggleSectionVisibility('faqs')}
          size="small"
        />
      </div>

      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">FAQs</h3>
        <p className="text-sm text-gray-600">
          What do clients often ask you? Create an FAQ section to help them feel prepared and comfortable before reaching out.
        </p>
      </div>

      {/* Inline Callout */}
      <InlineCallout
        text="Not sure what to include? Check out common FAQ examples and best practices in the Playbook."
        playbookTopic="policies-and-protection"
      />

      {/* Suggested FAQs */}
      {showSuggestions && (
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-1">Suggested FAQs for Practitioners</h4>
            <p className="text-xs text-gray-600">Click any question to add it, then customize the answer</p>
          </div>
          <div className="space-y-2">
            {availableSuggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleAddSuggestedFaq(suggestion)}
                className="w-full flex items-center justify-between px-4 py-3 bg-white border border-[#E5E7EB] rounded-lg hover:border-[#7c3aed] hover:bg-[#F5F3FF] transition-all duration-150 text-left group"
              >
                <span className="text-sm text-[#374151] group-hover:text-[#7c3aed] transition-colors">
                  {suggestion.question}
                </span>
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#F3F4F6] group-hover:bg-[#7c3aed] transition-colors">
                  <Plus className="w-4 h-4 text-[#9CA3AF] group-hover:text-white transition-colors" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* FAQ List */}
      {faqs.length > 0 && editingFaq === null && (
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <FAQCard
              key={faq.id}
              faq={faq}
              index={index}
              isFirst={index === 0}
              isLast={index === faqs.length - 1}
              onEdit={() => handleEditFaq(faq.id)}
              onDelete={() => setDeleteConfirm(faq.id)}
              onToggleActive={() => handleToggleActive(faq.id)}
              onMoveUp={() => handleReorder(faq.id, 'up')}
              onMoveDown={() => handleReorder(faq.id, 'down')}
            />
          ))}
        </div>
      )}

      {/* FAQ Form */}
      {editingFaq !== null && (
        <FAQForm
          faq={editingFaq === 'new' ? null : faqs.find(f => f.id === editingFaq)}
          isNew={editingFaq === 'new'}
          existingFaqs={faqs}
          onSave={(faqData) => {
            if (editingFaq === 'new') {
              // Add new FAQ
              const newFaq = {
                id: `faq_${Date.now()}`,
                ...faqData,
                order: faqs.length,
                createdAt: new Date().toISOString()
              };
              actions.updateContent('faqs', [...faqs, newFaq]);
            } else {
              // Update existing FAQ
              const updatedFaqs = faqs.map(f =>
                f.id === editingFaq ? { ...f, ...faqData } : f
              );
              actions.updateContent('faqs', updatedFaqs);
            }
            setEditingFaq(null);
          }}
          onCancel={() => setEditingFaq(null)}
        />
      )}

      {/* Empty State */}
      {faqs.length === 0 && editingFaq === null && (
        <div className="text-center py-8">
          <HelpCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-sm text-gray-600 mb-6">
            Clients always have questions. Add your most common ones here.
          </p>
        </div>
      )}

      {/* Add FAQ Button */}
      {editingFaq === null && (
        <button
          onClick={handleAddFaq}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-[#7c3aed] rounded-lg text-sm font-medium text-[#7c3aed] hover:bg-[#F5F3FF] hover:border-[#6d28d9] transition-all duration-150"
          style={{ fontSize: '14px', fontWeight: 500 }}
        >
          <Plus className="w-4 h-4" />
          Add FAQ
        </button>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <DeleteConfirmModal
          question={faqs.find(f => f.id === deleteConfirm)?.question}
          onConfirm={() => handleDeleteFaq(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
};

// FAQ Card Component
const FAQCard = ({ faq, index, isFirst, isLast, onEdit, onDelete, onToggleActive, onMoveUp, onMoveDown }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-purple-200 transition-colors">
      <div className="flex items-start gap-3">
        {/* Reorder Buttons */}
        <div className="flex flex-col gap-0.5 pt-1">
          <button
            onClick={onMoveUp}
            disabled={isFirst}
            className={`p-0.5 rounded transition-colors ${
              isFirst
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
            }`}
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            onClick={onMoveDown}
            disabled={isLast}
            className={`p-0.5 rounded transition-colors ${
              isLast
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
            }`}
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div
            onClick={() => setIsExpanded(!isExpanded)}
            className="cursor-pointer"
          >
            <h4 className="text-sm font-semibold text-gray-900 mb-1">
              {faq.question}
            </h4>
            {!isExpanded && (
              <p className="text-xs text-gray-500 line-clamp-1">
                {faq.answer}
              </p>
            )}
          </div>
          {isExpanded && (
            <p className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">
              {faq.answer}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Toggle
            enabled={faq.active}
            onChange={onToggleActive}
          />
          <button
            onClick={onEdit}
            className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// FAQ Form Component
const FAQForm = ({ faq, isNew, existingFaqs, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    question: faq?.question || '',
    answer: faq?.answer || '',
    active: faq?.active ?? true
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const MAX_QUESTION_LENGTH = 200;
  const MAX_ANSWER_LENGTH = 500;

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.question.trim()) {
      newErrors.question = 'Question is required';
    } else if (formData.question.length > MAX_QUESTION_LENGTH) {
      newErrors.question = `Question must be ${MAX_QUESTION_LENGTH} characters or less`;
    }

    if (!formData.answer.trim()) {
      newErrors.answer = 'Answer is required';
    } else if (formData.answer.length > MAX_ANSWER_LENGTH) {
      newErrors.answer = `Answer must be ${MAX_ANSWER_LENGTH} characters or less`;
    }

    // Check for duplicate question (case-insensitive)
    const isDuplicate = existingFaqs.some(
      f => f.id !== faq?.id &&
      f.question.toLowerCase().trim() === formData.question.toLowerCase().trim()
    );

    if (isDuplicate) {
      newErrors.question = 'This question already exists';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setTouched({ question: true, answer: true });
      return;
    }

    onSave(formData);
  };

  const questionLength = formData.question.length;
  const answerLength = formData.answer.length;
  const isQuestionOverLimit = questionLength > MAX_QUESTION_LENGTH;
  const isAnswerOverLimit = answerLength > MAX_ANSWER_LENGTH;
  const isAnswerNearLimit = answerLength > MAX_ANSWER_LENGTH * 0.9;

  return (
    <form onSubmit={handleSubmit} className="border border-purple-200 rounded-lg p-5 bg-purple-50/30 space-y-4">
      <h4 className="text-sm font-semibold text-gray-900">
        {isNew ? 'Add FAQ' : 'Edit FAQ'}
      </h4>

      {/* Question Field */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">
          Question <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.question}
          onChange={(e) => handleChange('question', e.target.value)}
          onBlur={() => handleBlur('question')}
          placeholder="Do you offer virtual sessions?"
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${
            (errors.question && touched.question) || isQuestionOverLimit
              ? 'border-red-400'
              : 'border-gray-300'
          }`}
        />
        {errors.question && touched.question && (
          <p className="mt-1 text-xs text-red-600">{errors.question}</p>
        )}
        <p className={`mt-1 text-xs ${
          isQuestionOverLimit ? 'text-red-600 font-medium' : 'text-gray-500'
        }`}>
          {questionLength}/{MAX_QUESTION_LENGTH} characters
          {isQuestionOverLimit && ` (-${questionLength - MAX_QUESTION_LENGTH} over limit)`}
        </p>
      </div>

      {/* Answer Field */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">
          Answer <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.answer}
          onChange={(e) => handleChange('answer', e.target.value)}
          onBlur={() => handleBlur('answer')}
          placeholder="Yes, I offer sessions via video call. Virtual sessions are just as powerful..."
          rows={5}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors resize-none ${
            (errors.answer && touched.answer) || isAnswerOverLimit
              ? 'border-red-400'
              : 'border-gray-300'
          }`}
        />
        {errors.answer && touched.answer && (
          <p className="mt-1 text-xs text-red-600">{errors.answer}</p>
        )}

        {/* Character Counter with Progress Bar */}
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <p className={`text-xs ${
              isAnswerOverLimit
                ? 'text-red-600 font-medium'
                : isAnswerNearLimit
                ? 'text-amber-600 font-medium'
                : 'text-gray-500'
            }`}>
              {answerLength}/{MAX_ANSWER_LENGTH} characters
              {isAnswerOverLimit && ` (-${answerLength - MAX_ANSWER_LENGTH} over limit)`}
            </p>
          </div>
          <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${
                isAnswerOverLimit
                  ? 'bg-red-500'
                  : isAnswerNearLimit
                  ? 'bg-amber-500'
                  : 'bg-purple-500'
              }`}
              style={{ width: `${Math.min((answerLength / MAX_ANSWER_LENGTH) * 100, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Active Toggle */}
      <div className="flex items-center justify-between p-3 bg-white rounded-lg">
        <div>
          <p className="text-sm font-medium text-gray-900">Display on Website</p>
          <p className="text-xs text-gray-600">Toggle off to hide this FAQ without deleting it</p>
        </div>
        <Toggle
          enabled={formData.active}
          onChange={(value) => handleChange('active', value)}
        />
      </div>

      {/* Buttons */}
      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isQuestionOverLimit || isAnswerOverLimit}
          className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isNew ? 'Add FAQ' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
};

// Delete Confirmation Modal
const DeleteConfirmModal = ({ question, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl p-6" style={{ maxWidth: '400px', width: '100%' }}>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Delete FAQ?</h3>
        <p className="text-sm text-gray-600 mb-1">Are you sure you want to delete:</p>
        <p className="text-sm font-semibold text-gray-900 mb-6">"{question}"</p>
        <p className="text-xs text-gray-500 mb-6">This cannot be undone.</p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default FAQsPanel;
